import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function Signup() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(email, password, fullName);
      toast.success("Welcome! Your 30-day trial has started.");
      navigate("/app/onboarding", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="signup-page">
      <div className="w-full max-w-md fade-in">
        <Link to="/" className="flex items-center gap-2.5 mb-8 justify-center">
          <span className="w-10 h-10 rounded-full bg-[#4A6148] flex items-center justify-center text-white font-wordmark tracking-tight text-xs font-bold leading-none">WYB</span>
          <span className="font-wordmark text-[#2C302E] text-lg leading-none">What&rsquo;s Your Balance<span className="font-wordmark-italic text-[#4A6148]">?</span></span>
        </Link>
        <div className="card-soft p-8">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E] text-xs font-semibold px-3 py-1">
            <Sparkles size={12} /> 1 month free · No card upfront
          </div>
          <h1 className="font-heading text-2xl font-medium text-[#2C302E] mt-4">Create your account</h1>
          <p className="text-sm text-[#59605C] mt-1">Takes about 30 seconds.</p>

          <button
            type="button"
            onClick={googleSignup}
            data-testid="signup-google-button"
            className="mt-6 w-full btn-secondary"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.5 14.7 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12S6.7 21.6 12 21.6c6.9 0 11.5-4.9 11.5-11.7 0-.8-.1-1.4-.2-2H12z" />
            </svg>
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-[#8A918D]">
            <div className="h-px flex-1 bg-[#EBE6E0]" /> or use email <div className="h-px flex-1 bg-[#EBE6E0]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#59605C] mb-2">Your name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="input-soft"
                data-testid="signup-name-input"
                placeholder="Alex Rivera"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#59605C] mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-soft"
                data-testid="signup-email-input"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#59605C] mb-2">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-soft"
                data-testid="signup-password-input"
                placeholder="At least 6 characters"
              />
            </div>
            {error && (
              <div className="rounded-xl bg-[#FDF2F0] border border-[#F5D8D4] text-[#8C3A30] text-sm p-3" data-testid="signup-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="signup-submit-button"
            >
              {loading ? "Creating account…" : "Start free trial"}
            </button>
          </form>

          <div className="mt-6 text-sm text-center text-[#59605C]">
            Already have an account?{" "}
            <Link to="/login" data-testid="signup-to-login" className="text-[#4A6148] font-medium hover:underline">
              Log in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
